import * as React from 'react'

import { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl'
import { action, makeObservable, observable, reaction } from 'mobx'

import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import { projectToOverlay } from '~/client/src/shared/components/MapBoxViewer/projectToOverlay'
import Markup from '~/client/src/shared/models/Markup'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { isTextEntryTarget } from '~/client/src/shared/utils/isTextEntryTarget'
import {
  isBaseSurfaceActive,
  isClaimOnTop,
  KeyboardClaim,
} from '~/client/src/shared/utils/keyboardClaim'

import MarkupDrawController, {
  ArrowheadPosition,
  DEFAULT_MARKUP_STYLE,
  DrawType,
  IMarkupFeatureData,
  IMarkupSelection,
  LineType,
  MarkupKind,
  MarkupTool,
  NO_FILL,
  ShapeKind,
  StrokeStyle,
} from './MarkupDrawController'
import { MarkupEditorTextChannel } from './markupEditorTextChannel'
import { MarkupEditorTextSwap } from './markupEditorTextSwap'
import {
  deriveBorderColor,
  DRAW_DEFAULT_STYLE,
  HIGHLIGHTER_DEFAULT_STYLE,
  LINE_DEFAULT_STYLE,
  SHAPE_DEFAULT_STYLE,
} from './markupSegments'
import { IShortcutGroup } from './MarkupShortcutsLegend'
import { MarkupTextCapture } from './markupTextCapture'
import { textScaleForZoom } from './markupTextScale'
import MarkupTextStore, {
  DEFAULT_FONT_SIZE,
  ITextBox,
  TextAlign,
} from './MarkupTextStore'
import { TextTapRecord } from './markupTextTap'
import { ArrowheadType, ERASER_THICKNESS, FillStyle } from './markupTools'
import { queryTextMarkups } from './TextMarkupGLLayer'

export interface ICameraViewport {
  latitude: number
  longitude: number
  bearing: number
  pitch: number
  zoom: number
}

// Opaque to the editor; the parent (WorkflowFormPreciseLocation) owns it.
export type MarkupLocationSnapshot = ICameraViewport | null

interface IMarkupHistoryEntry {
  geometry: IMarkupFeatureData[]
  text: ITextBox[]
  location: MarkupLocationSnapshot
}

// Stashed when the user leaves a globe so returning restores its content + undo timeline.
interface IGlobeEditState {
  loadedIds: string[]
  baselineSig: string
  features?: IMarkupFeatureData[]
  history?: IMarkupHistoryEntry[]
  index?: number
  headJson?: string
}

// One object, not positional callbacks: five of the six handles share the
// () => void signature, so a transposition would type-check silently.
export interface IMarkupEditorHandles {
  save: () => void
  recordLocationChange: () => void
  getFeatures: () => IMarkupFeatureData[]
  discardChanges: () => void
  resetLocationBaseline: () => void
  exitDrawing: () => void
}

export interface IMarkupEditorDeps {
  mapBoxViewerStore: MapBoxViewerStore
  markupsStore: MarkupsStore
  sitePermitId: string
  projectId: string
  // Creation flow: buffer markups locally (no SaveMarkup) under the temp
  // sitePermitId; the creation store flushes them to the real id post-create.
  deferred?: boolean
  onClose: () => void
  // resetLocationBaseline re-captures the current state as the undo baseline
  // (the always-on Workflows-map session anchors each creation's pin as history[0]).
  onReady?: (handles: IMarkupEditorHandles | null) => void
  // Dirty = uncommitted markups and/or a recorded pin-location move (for the close guard).
  onDirtyChange?: (dirty: boolean) => void
  // Reads the committed pin location to fold into each history snapshot, and
  // re-applies it on undo/redo. Both absent → snapshots carry `location: null`
  // and undo/redo leaves location untouched.
  getLocation?: () => MarkupLocationSnapshot
  onRestoreLocation?: (location: MarkupLocationSnapshot) => void
  // Copy-from-other-workflow: the reference markup (other workflow, same map)
  // currently picked as a copy source, and a setter to clear it when an editable
  // markup is selected instead. Cmd/Ctrl+C copies it into THIS workflow.
  copySelectedId?: string | null
  onCopySelect?: (id: string | null) => void
  // Precise-location edit surfaced as a toolbar pin tool; the action + state live
  // in the parent (WorkflowFormPreciseLocation). showLocationTool renders the
  // tool; isLocationEditActive highlights it (and the editor goes inert).
  showLocationTool?: boolean
  isLocationEditActive?: boolean
  onEditPreciseLocation?: () => void
  // Host-specific shortcut groups prepended to the legend (e.g. the Workflows
  // map's create gesture).
  legendExtraGroups?: IShortcutGroup[]
  // Keep terra-draw's GL layers out of the map until drawing is engaged (tool
  // pick / paste / ensureDrawing()) — idle they cost ~10 layers on every frame.
  deferDrawingUntilEngaged?: boolean
  // Fired when the user starts interacting with markup tools (picks a drawing
  // tool) — the parent deselects the location pin (hides its blue button).
  onMarkupActivity?: () => void
  // Reports whether the editor is idle on the Select tool (no markup/text
  // selected). The parent only lets the location pin be clicked when idle, so
  // pin selection never interferes with drawing or editing a markup.
  onIdleSelectChange?: (isIdle: boolean) => void
  // True while a Text markup is typed into or selected — the parent keeps the
  // view on the text then instead of re-centering on the pin.
  onTextSubjectChange?: (isText: boolean) => void
  // The hosting surface's claim. Absent = the base surface, whose keys go quiet
  // while an overlay holds the keyboard.
  keyboardClaim?: KeyboardClaim | null
  // The host's layers drawn above a resting markup: a click on them selects nothing beneath.
  yieldToLayerIds?: string[]
  // The same for text markup layers, which queryRenderedFeatures cannot see.
  yieldToTextLayerIds?: string[]
  // The host's session text channel, fed while this editor is mounted.
  editorText?: MarkupEditorTextChannel
}

// Style captured when a mobile picker sheet opens, restored on Cancel.
interface IStylePreviewSnapshot {
  geometry?: {
    color: string
    strokeWidth: number
    strokeStyle: StrokeStyle
    fillColor: string
    fillStyle: FillStyle
    arrowheadStart: ArrowheadType
    arrowheadEnd: ArrowheadType
    borderOverridden: boolean
  }
  text?: { color: string; fillColor: string; fontSize: number }
}

// Live-preview commit controls handed to a mobile picker sheet: begin on open,
// apply commits one undo entry, cancel restores the pre-open style.
export interface IStylePreview {
  begin: () => void
  apply: () => void
  cancel: () => void
}

// Tall enough to keep the rotate pin visible above the markup (bbox edge + 22px orbit + 6px radius).
export const TOOLBAR_GAP = 44

const LEGEND_HINT_SEEN_KEY = 'markupShortcutsHintSeen'
const VERTEX_EDIT_HINT_SEEN_KEY = 'markupVertexEditHintSeen'
const VERTEX_DELETE_HINT_SEEN_KEY = 'markupVertexDeleteHintSeen'

// Single pass, not Math.min(...arr): a dense vertex array blows the call-argument limit.
function screenBounds(screens: { x: number; y: number }[]): {
  minX: number
  maxX: number
  minY: number
  maxY: number
} {
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const { x, y } of screens) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, maxX, minY, maxY }
}

export default class MarkupEditorStore {
  // Mirrors deps.isLocationEditActive (parent-owned pin-edit sub-mode) so the
  // observer shells re-render when it toggles.
  @observable public isLocationEditActive = false
  // Mirrors deps.showLocationTool: deps is a plain field, so a bare getter would
  // never re-render the observer shells when the parent flips this on.
  @observable public showLocationTool = false
  @observable public activeTool: MarkupTool = 'select'
  @observable public shapeKind: ShapeKind = 'rectangle'
  @observable public lineType: LineType = 'line'
  @observable public drawType: DrawType = 'pen'
  @observable public color: string = DEFAULT_MARKUP_STYLE.color
  @observable public strokeWidth: number = DEFAULT_MARKUP_STYLE.strokeWidth
  @observable public fillColor: string = SHAPE_DEFAULT_STYLE.fillColor
  @observable public fillStyle: FillStyle = 'solid'
  // Whether the user has explicitly set the (default) border color — once true,
  // fill changes stop auto-deriving the border. Resets when re-entering Shape.
  @observable public borderOverridden = false
  @observable public canUndo = false
  @observable public canRedo = false
  @observable public showLegend = false
  @observable public showLegendHint = false
  private legendHintTimer: number = null
  // Coaching tip for a selected polygon/polyline (it has a manual move→edit step), until used once.
  @observable public showVertexEditHint = false
  private vertexEditHintTimer: number = null
  @observable public vertexHintScreen: { x: number; y: number } = null
  // Touch/mobile build (set once by the facade). Gates the mobile-only "long-press
  // a point to remove it" coaching tip — both its show and its render.
  @observable public isMobileEnv = false
  @observable public showVertexDeleteHint = false
  private vertexDeleteHintTimer: number = null
  // Teardown for an in-flight toolbar drag, run on unmount so its window
  // listeners don't leak if the editor closes mid-drag.
  private toolbarDragCleanup: (() => void) | null = null
  // True while the user is moving/resizing the selected markup — the context
  // toolbar hides during the drag and re-anchors above the markup on release.
  @observable public interacting = false
  // True while a polygon is being drawn (≥1 vertex placed) — swaps the Shape
  // sub-toolbar for the "Finish shaping" / Trash controls.
  @observable public polygonDrawing = false
  // Same, for the polyline (line tool's multi-click sub-type) — drives the
  // "Finish line" / Trash controls and Enter-to-finish.
  @observable public polylineDrawing = false

  @observable public selection: IMarkupSelection = null
  @observable public selectionScreen: { x: number; y: number } = null
  // Screen anchor for the copy toolbar of a picked foreign (other-workflow)
  // reference markup; tracks pan/zoom like selectionScreen.
  @observable.ref public foreignSelectedScreen: { x: number; y: number } = null
  // The picked foreign (copy-source) markup id. Mirrored from deps in syncDeps —
  // deps is a plain field, so the copy pill (gated on this) needs an observable to
  // re-render the instant one is picked.
  @observable public copySelectedId: string | null = null
  // Once the user drags the context toolbar (grip handle), it pins to a fixed
  // SCREEN position and unlinks from the item — no longer following item moves or
  // map pan/zoom. null = linked (tracks the item's anchor). Reset per selection.
  @observable.ref public pinnedToolbarPos: { x: number; y: number } = null
  // Same drag-to-pin for the TEXT toolbar (it has its own grip); reset when the
  // selected text box changes (reaction in mount).
  @observable.ref public pinnedTextToolbarPos: { x: number; y: number } = null
  // Screen positions of a selected rectangle's 4 corner resize handles (our own
  // constrained handles; Terra Draw treats rectangles as move-only).
  @observable.ref public rectangleHandleScreens: { x: number; y: number }[] = []
  // The 4 corner screens of a selected circle's screen-aligned bounding square
  // (circle is move-only in Terra Draw; dragging a corner sets the radius). The
  // square box itself is drawn via selectionBoxScreens, like the path box.
  @observable.ref public circleHandleScreens: { x: number; y: number }[] = []
  // Oriented (Excalidraw-style) selection box for a freehand stroke: the 4
  // corner screens of the plane-oriented chrome box (projected per-point, so
  // it hugs the stroke at any map pitch/bearing). Order [TL, TR, BR, BL] in
  // the box's own frame; drag a corner to stretch-scale along its axes.
  @observable.ref public freehandHandleScreens: { x: number; y: number }[] = []
  // Selection bounding box for a path markup (line/polyline/polygon/arrow): the 4
  // projected chrome-box corners, drawn so the rotate pin hangs off a visible
  // frame (rect's casing already is its box; freehand has its own). Non-interactive
  // — paths resize by vertex editing, not bbox scaling.
  @observable.ref public selectionBoxScreens: { x: number; y: number }[] = []
  // The path box's PLANE-space orientation (radians), tracked CONTINUOUSLY so the
  // rotate pin orbits to the release position without flipping: seeded from the
  // min-area orientation, then advanced by the controller's rotation delta each
  // frame. selectionBoxBboxAngle is the bbox angle (rad) it was last advanced
  // against. All reset when the selection changes.
  private selectionBoxAngle: number | null = null
  private selectionBoxBboxAngle: number | null = null
  private selectionBoxId: string | null = null
  // Screen position of the rotate handle — floats below the selection (the
  // toolbar owns the top); null for circles (rotation is meaningless) and text.
  @observable.ref public rotateHandleScreen: { x: number; y: number } = null
  // The bbox-edge point the rotate handle hangs off — anchors the stem line
  // that visually ties the handle to the selection (Google-Slides style).
  @observable.ref public rotateStemScreen: { x: number; y: number } = null
  // Overlay-relative coords (for the text context toolbar, which lives in the
  // sibling overlay above the map chrome).
  @observable.ref public textScreens = new Map<
    string,
    { x: number; y: number }
  >()
  // Map-container-relative coords (for the text boxes themselves, portaled into
  // the map container so they sit beneath the map chrome like the read-only
  // preview's <Marker>s).
  @observable.ref public textMapScreens = new Map<
    string,
    { x: number; y: number }
  >()

  public readonly textStore = new MarkupTextStore(
    () => this.captureHistory(),
    () => this.currentGlobeViewId,
  )
  private readonly textTaps = new TextTapRecord()
  public readonly textSwap = new MarkupEditorTextSwap(this.textStore, () =>
    this.repositionText(),
  )
  private textCapture: MarkupTextCapture | null = null
  public readonly overlayRef = React.createRef<HTMLDivElement>()
  public readonly toolbarRef = React.createRef<HTMLDivElement>()
  public readonly textToolbarRef = React.createRef<HTMLDivElement>()
  private disposeTextSelectionReaction: () => void = null
  private disposeToolReaction: () => void = null
  private disposeIdleSelectReaction: () => void = null
  private disposeTextSubjectReaction: () => void = null
  private disposeGlobeReaction: () => void = null
  private disposeMapChange: () => void = null
  private controller: MarkupDrawController = null
  public map: MapboxMap = null
  // The editor edits one globe at a time; each globe's baseline, unsaved working copy,
  // and undo timeline live here so switching away and back restores all three.
  private activeGlobeViewId: string | undefined = undefined
  private editByGlobe = new Map<string, IGlobeEditState>()
  private initRetry: ReturnType<typeof setTimeout> = null
  private bindGeneration = 0
  // Latched by ensureDrawing() so an engagement that races map readiness still
  // boots the controller once the map arrives.
  private drawingEngaged = false

  // Snapshot-based undo/redo over both geometry (controller) and text (store).
  // Index points at the live state; undo/redo step through the array.
  private history: IMarkupHistoryEntry[] = []
  private historyIndex = -1
  // Serialized form of history[historyIndex], kept in sync on every index move so
  // commitHistory's dedup compares against it instead of re-stringifying the head.
  private headJson: string | null = null
  private captureTimer: ReturnType<typeof setTimeout> = null
  private copySelectTimer: ReturnType<typeof setTimeout> = null
  private isRestoring = false
  // Mobile picker-sheet preview: while previewing, style changes apply live to the
  // map but are NOT committed to undo history; Apply commits one entry, Cancel
  // restores the style captured at open. See stylePreview.
  private previewing = false
  private previewOriginal: IStylePreviewSnapshot | null = null

  // In-app clipboard for copy/paste (duplicate). Snapshots the data at copy time
  // so it survives later edits/deletes of the source. Editor-instance only.
  private clipboard:
    | { type: 'geometry'; data: IMarkupFeatureData }
    | { type: 'text'; data: ITextBox }
    | null = null

  public constructor(private deps: IMarkupEditorDeps) {
    this.textStore.onSelectionChange = this.textSwap.onSelectionChange
    makeObservable(this)
    this.isLocationEditActive = !!deps.isLocationEditActive
    this.showLocationTool = !!deps.showLocationTool
  }

  @action.bound
  private setLocationEditActive(value: boolean): void {
    // Entering pin-edit makes the markup editor inert (no drawing) so map drag
    // moves the pin.
    if (value && !this.isLocationEditActive) this.selectTool('select')
    this.isLocationEditActive = value
  }

  public mount(): void {
    this.waitForMapAndInit()
    window.addEventListener('keydown', this.handleKeyDown)
    // A recovered map is a NEW mapbox instance, and react-map-gl removes the old one on the way
    // (STX1-1068): everything bound to it — the controller, the terra-draw adapter, the listeners
    // — is dead.
    // The swap notifies twice — null, then the new ref — and the halves are not
    // interchangeable. React detaches the ref before react-map-gl's cleanup calls
    // map.remove(), so the null edge is the last moment terra-draw and the listeners can
    // come off a live instance; leaving them attached lets them call into a dead style.
    // The stash belongs there too: after detachMap has nulled the controller it would
    // capture a text-only feature list and overwrite the working copy.
    this.disposeMapChange = this.deps.mapBoxViewerStore.onMapChange(map => {
      if (!map) {
        this.stashActiveGlobe()
        this.detachMap()
        return
      }
      this.waitForMapAndInit()
    })
    this.disposeTextSelectionReaction = reaction(
      () => this.textStore.selectedId,
      this.resetTextToolbarPin,
    )
    this.disposeIdleSelectReaction = reaction(
      () => this.isIdleSelect,
      isIdle => {
        this.deps.onIdleSelectChange?.(isIdle)
        this.deps.mapBoxViewerStore.setMarkupEditing(!isIdle)
        // Quick-zoom is safe only when idle (select tool, nothing selected); a drawing
        // tool, the text tool, or any geometry/text selection makes a one-finger drag a
        // draw/move, so suppress it then.
        this.controller?.setQuickZoomEnabled(isIdle)
      },
      { fireImmediately: true },
    )
    this.disposeTextSubjectReaction = reaction(
      () => this.isTextSubject,
      isText => this.deps.onTextSubjectChange?.(isText),
      { fireImmediately: true },
    )
  }

  @action
  public syncDeps(next: IMarkupEditorDeps): void {
    const prev = this.deps
    this.deps = next
    this.showLocationTool = !!next.showLocationTool
    this.setLocationEditActive(!!next.isLocationEditActive)
    if (prev.copySelectedId !== next.copySelectedId) {
      this.applyCopySelection(next.copySelectedId ?? null)
    }
  }

  // Picking a foreign markup clears any editable selection (copy ≠ edit), mirrors the
  // id into an observable so the copy pill re-renders, and re-anchors the toolbar.
  @action.bound
  private applyCopySelection(id: string | null): void {
    this.copySelectedId = id
    if (id) {
      this.controller?.deselect()
      this.textStore.select(null)
    }
    this.repositionForeignCopy()
  }

  public unmount(): void {
    this.deps.editorText?.detach(this.textSwap)
    this.textSwap.dispose()
    if (this.initRetry) clearTimeout(this.initRetry)
    if (this.captureTimer) clearTimeout(this.captureTimer)
    if (this.copySelectTimer) clearTimeout(this.copySelectTimer)
    this.detachMap()
    window.removeEventListener('keydown', this.handleKeyDown)
    this.toolbarDragCleanup?.()
    if (this.legendHintTimer) clearTimeout(this.legendHintTimer)
    if (this.vertexEditHintTimer) clearTimeout(this.vertexEditHintTimer)
    if (this.vertexDeleteHintTimer) clearTimeout(this.vertexDeleteHintTimer)
    this.disposeTextSelectionReaction?.()
    this.disposeIdleSelectReaction?.()
    this.disposeTextSubjectReaction?.()
    this.disposeMapChange?.()
    this.deps.mapBoxViewerStore.setMarkupEditing(false)
  }

  // Everything attachMap and initController bound to one instance, undone. Split out of
  // unmount so a recovery can rebind without ending the edit session.
  private detachMap(): void {
    this.bindGeneration++
    this.textCapture?.dispose()
    this.textCapture = null
    this.map?.off('move', this.handleMapMove)
    this.map?.off('render', this.handleMapRender)
    this.map?.off('click', this.handleMapClick)
    // The map object outlives its own DOM: remove() voids _canvas and _container while the instance
    // stays reachable, and React deletes the parent map before this child unmounts.
    this.map
      ?.getCanvas()
      ?.removeEventListener('pointerdown', this.handleCanvasPointerDown)
    this.map
      ?.getContainer()
      ?.classList.remove('markup-editing', 'markup-dragging', 'markup-drawing')
    this.disposeToolReaction?.()
    this.disposeToolReaction = null
    this.disposeGlobeReaction?.()
    this.disposeGlobeReaction = null
    this.controller?.destroy()
    this.controller = null
    this.map = null
  }

  // First-entry nudge, once per browser. Called by the desktop shell after mount
  // so the hint tooltip opens with the trigger laid out — opening at mount anchors
  // it at 0,0.
  @action.bound
  public maybeShowLegendHint(): void {
    if (localStorage.getItem(LEGEND_HINT_SEEN_KEY)) return
    localStorage.setItem(LEGEND_HINT_SEEN_KEY, '1')
    this.showLegendHint = true
    this.legendHintTimer = window.setTimeout(this.dismissLegendHint, 6000)
  }

  @action.bound
  private dismissLegendHint(): void {
    this.showLegendHint = false
    if (this.legendHintTimer) {
      clearTimeout(this.legendHintTimer)
      this.legendHintTimer = null
    }
  }

  @action.bound
  public setLegendOpen(open: boolean): void {
    this.showLegend = open
    if (open) this.dismissLegendHint()
  }

  // Show the "click again to edit points" tip when a polygon/polyline is selected
  // (it has a manual move→edit step), until the user has done it once. Re-dismisses
  // first so a fresh non-eligible selection clears any lingering tip.
  @action.bound
  private maybeShowVertexEditHint(): void {
    this.dismissVertexEditHint()
    if (!this.selection?.vertexEditHint) return
    if (localStorage.getItem(VERTEX_EDIT_HINT_SEEN_KEY)) return
    this.showVertexEditHint = true
    this.vertexEditHintTimer = window.setTimeout(
      this.dismissVertexEditHint,
      6000,
    )
  }

  @action.bound
  private dismissVertexEditHint(): void {
    this.showVertexEditHint = false
    if (this.vertexEditHintTimer) {
      clearTimeout(this.vertexEditHintTimer)
      this.vertexEditHintTimer = null
    }
  }

  // The user advanced a selection to vertex edit (the 2nd click) — retire the tip
  // for good, and (mobile) offer the "long-press to remove a point" tip next.
  @action.bound
  private handleEnterVertexEdit(): void {
    localStorage.setItem(VERTEX_EDIT_HINT_SEEN_KEY, '1')
    this.dismissVertexEditHint()
    this.maybeShowVertexDeleteHint()
  }

  @action.bound
  public setMobileEnv(value: boolean): void {
    this.isMobileEnv = value
  }

  // Mobile-only: on entering vertex-edit, teach long-press-to-delete (touch has no
  // right-click), until the user removes a vertex once.
  @action.bound
  private maybeShowVertexDeleteHint(): void {
    this.dismissVertexDeleteHint()
    if (!this.isMobileEnv) return
    if (localStorage.getItem(VERTEX_DELETE_HINT_SEEN_KEY)) return
    this.showVertexDeleteHint = true
    this.vertexDeleteHintTimer = window.setTimeout(
      this.dismissVertexDeleteHint,
      6000,
    )
  }

  @action.bound
  private dismissVertexDeleteHint(): void {
    this.showVertexDeleteHint = false
    if (this.vertexDeleteHintTimer) {
      clearTimeout(this.vertexDeleteHintTimer)
      this.vertexDeleteHintTimer = null
    }
  }

  // The user removed a vertex (the long-press) — retire the tip for good.
  @action.bound
  private handleVertexDeleted(): void {
    localStorage.setItem(VERTEX_DELETE_HINT_SEEN_KEY, '1')
    this.dismissVertexDeleteHint()
  }

  // Hosted by both an overlay and the surface underneath it, so this is the one
  // place the two rules meet.
  private get ownsKeyboard(): boolean {
    const claim = this.deps.keyboardClaim
    return claim ? isClaimOnTop(claim) : isBaseSurfaceActive()
  }

  // Enter (commit) / Escape (cancel) release a selected text box from anywhere —
  // a window listener, not the box's contentEditable, because focus may have
  // left the box (selected-but-not-editing, or after touching a toolbar control).
  // Skip when a real form field (color hex input, sliders) owns the keystroke;
  // Shift+Enter falls through so multi-line text still works.
  @action.bound
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.ownsKeyboard) {
      return
    }
    // A key another window handler already consumed (the composer's Enter/Esc, the
    // move-pin pill) must not also act here. Every handler in the ladder checks this,
    // so who wins no longer depends on listener registration order — which differs
    // between desktop (editor mounts first) and mobile (composer registers first).
    if (e.defaultPrevented) {
      return
    }
    const target = e.target as HTMLElement
    const tag = target?.tagName
    // Deliberately NOT isEditableOrControlTarget: a focused toolbar button is
    // not text entry, and undo/copy/paste must keep working after a button click.
    const isTextEntry = isTextEntryTarget(target)

    // While Move Pin Position is active, Esc/Enter belong to the parent's
    // Cancel/Done pill — swallow them so the editor's own handlers don't also fire.
    if (
      this.isLocationEditActive &&
      (e.key === 'Escape' || e.key === 'Enter')
    ) {
      return
    }

    // Undo / redo (Cmd/Ctrl+Z, +Shift = redo). While typing in a text box, let the
    // browser's native text undo win.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
      if (isTextEntry) return
      e.preventDefault()
      if (e.shiftKey) this.redo()
      else this.undo()
      return
    }

    // Copy / paste (duplicate) the selected markup. Skipped while typing in a
    // field or editing a text box — native text copy/paste wins there.
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !isTextEntry) {
      const key = e.key.toLowerCase()
      if (key === 'c' && this.copySelectedMarkup()) {
        e.preventDefault()
        return
      }
      if (key === 'v' && this.clipboard) {
        e.preventDefault()
        this.pasteMarkup()
        return
      }
    }

    // `I` toggles the shortcut legend; Escape closes it first. Both skipped while
    // typing in a field/text box or when a browser modifier is held. The legend
    // only mounts on desktop, so the toggle is a no-op chip on mobile.
    if (!isTextEntry && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (e.key.toLowerCase() === 'i') {
        e.preventDefault()
        this.setLegendOpen(!this.showLegend)
        return
      }
      if (this.showLegend && e.key === 'Escape') {
        e.preventDefault()
        this.setLegendOpen(false)
        return
      }
    }

    // Single-key tool shortcuts (R/O/L/⇧L/P/T/D). Skipped while typing in a field
    // or text box, and when a browser modifier is held (Cmd+R, Cmd+T, …).
    if (
      !isTextEntry &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      this.applyToolShortcut(e)
    ) {
      e.preventDefault()
      return
    }

    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    // Drawing a polygon/polyline: Enter finishes the shape, Escape discards it.
    if (this.polygonDrawing || this.polylineDrawing) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (this.polygonDrawing) this.finishPolygon()
        else this.finishPolyline()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        if (this.polygonDrawing) this.cancelPolygon()
        else this.cancelPolyline()
      }
      return
    }
    const id = this.textStore.selectedId
    if (id) {
      // Del removes a selected box; while editing it must still erase characters.
      if (
        this.textStore.editingId !== id &&
        (e.key === 'Delete' || e.key === 'Backspace')
      ) {
        e.preventDefault()
        this.deleteSelectedText()
        return
      }
      if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault()
        this.handleTextDone(id)
      }
      return
    }
    // A geometry markup is selected: Del deletes it, Esc steps back one stage —
    // vertex-edit → move (handles hide, stays selected) → deselected.
    if (this.selection) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        this.deleteSelectedMarkup()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        if (!this.controller?.stepDownToMove()) this.controller?.deselect()
      }
      return
    }
    // Nothing selected: Esc returns to the select tool if a drawing tool is active.
    if (e.key === 'Escape' && this.activeTool !== 'select') {
      e.preventDefault()
      this.selectTool('select')
    }
  }

  // Shift a lng/lat down-right by `px` in screen space (zoom-aware) for paste offset.
  private offsetLngLat(
    lng: number,
    lat: number,
    px = 10,
  ): { lng: number; lat: number } {
    if (!this.map) return { lng, lat }
    const p = this.map.project([lng, lat])
    const u = this.map.unproject([p.x + px, p.y + px])
    return { lng: u.lng, lat: u.lat }
  }

  // Copy the selected markup (geometry or text) into the in-app clipboard.
  // Returns false when nothing is selected so the key handler leaves Cmd+C native.
  @action.bound
  private copySelectedMarkup(): boolean {
    // A reference markup (other workflow, same map) picked via its code chip —
    // copy it into THIS workflow. Explicit pick, so it takes priority; selecting
    // an editable markup clears it (handleSelectionChange), so they don't clash.
    const foreignId = this.copySelectedId
    if (foreignId) {
      const markup = this.markupsStore.byId.get(foreignId)
      if (!markup) return false
      if (markup.kind === 'text') {
        this.clipboard = {
          type: 'text',
          data: {
            id: markup.id,
            lng: markup.coordinates[0]?.longitude ?? 0,
            lat: markup.coordinates[0]?.latitude ?? 0,
            text: markup.text ?? '',
            color: markup.color ?? DEFAULT_MARKUP_STYLE.color,
            fontSize: markup.fontSize ?? DEFAULT_FONT_SIZE,
            bold: markup.bold ?? false,
            italic: markup.italic ?? false,
            underline: markup.underline ?? false,
            textAlign: (markup.textAlign as TextAlign) ?? 'left',
            boxWidth: markup.boxWidth ?? null,
            rotation: markup.rotation ?? 0,
            fillColor: markup.fillColor ?? NO_FILL,
          },
        }
      } else {
        this.clipboard = { type: 'geometry', data: this.toFeatureData(markup) }
      }
      return true
    }
    if (this.selection) {
      const data = this.controller
        ?.getMarkupFeatures()
        .find(f => f.id === this.selection.id)
      if (!data) return false
      this.clipboard = { type: 'geometry', data: { ...data } }
      return true
    }
    const box = this.textStore.selected
    if (box) {
      this.clipboard = { type: 'text', data: { ...box } }
      return true
    }
    return false
  }

  // Paste a clone offset ~10px down-right, already selected. Repeated pastes
  // cascade (the clipboard advances to the last clone). Recorded in undo/redo via
  // the controller/text-store onChange → captureHistory.
  @action.bound
  private pasteMarkup(): void {
    if (!this.clipboard) return
    // Copy-from-other-workflow can be the first drawing action of the session.
    this.ensureDrawing()
    if (this.clipboard.type === 'geometry') {
      const src = this.clipboard.data
      const first = src.coordinates[0]
      const shifted = first
        ? this.offsetLngLat(first.longitude, first.latitude)
        : null
      const dLng = shifted ? shifted.lng - first.longitude : 0
      const dLat = shifted ? shifted.lat - first.latitude : 0
      const clone: IMarkupFeatureData = {
        ...src,
        id: BsonObjectId.generate(),
        // Paste lands on the current globe (clipboard survives globe switches).
        globeViewId: this.currentGlobeViewId,
        coordinates: src.coordinates.map(c => ({
          longitude: c.longitude + dLng,
          latitude: c.latitude + dLat,
        })),
      }
      this.controller?.addAndSelectMarkup(clone)
      this.clipboard = { type: 'geometry', data: clone }
    } else {
      const src = this.clipboard.data
      const clone: ITextBox = {
        ...src,
        id: BsonObjectId.generate(),
        // Larger offset than geometry: a 10px shift on a small label reads as no
        // offset, so stagger text clones enough to clearly stand apart.
        ...this.offsetLngLat(src.lng, src.lat, 20),
      }
      this.textStore.addBox(clone)
      this.repositionText()
      this.clipboard = { type: 'text', data: clone }
    }
  }

  // Init off the Mapbox map's own readiness rather than store observables: by
  // the time this mounts the style may already be loaded (no change for a
  // reaction/autorun to fire on), so we check map.isStyleLoaded() directly and
  // otherwise wait for the next idle.
  private readonly waitForMapAndInit = (): void => {
    if (this.controller) return
    // Cleared first: a rebind re-enters this while the previous retry is still pending.
    if (this.initRetry) clearTimeout(this.initRetry)
    this.initRetry = null
    const map = this.deps.mapBoxViewerStore.mapRef?.getMap?.()
    if (!map) {
      this.initRetry = setTimeout(this.waitForMapAndInit, 120)
      return
    }
    if (map.isStyleLoaded()) {
      this.attachAndMaybeInit(map)
    } else {
      const generation = this.bindGeneration
      map.once('idle', () => {
        // idle outlives both unmount and a rebind, and detachMap nulls the controller,
        // so the controller check alone would let a dead editor attach to a live map.
        if (generation !== this.bindGeneration) return
        if (!this.controller) this.attachAndMaybeInit(map)
      })
    }
  }

  private attachAndMaybeInit(map: MapboxMap): void {
    if (!this.map) this.attachMap(map)
    if (!this.deps.deferDrawingUntilEngaged || this.drawingEngaged) {
      this.initController(map)
    }
  }

  // Boot terra-draw on demand (tool pick, paste, creation start). Synchronous
  // when the style is loaded — the caller can use the controller right after.
  public ensureDrawing(): void {
    if (this.controller) return
    this.drawingEngaged = true
    if (this.map) this.initController(this.map)
    // No map yet: mount's retry loop reaches attachAndMaybeInit, which now proceeds.
  }

  private get markupsStore(): MarkupsStore {
    return this.deps.markupsStore
  }

  // The id observable, not selectedGlobe (the object, undefined until it loads).
  private get currentGlobeViewId(): string | undefined {
    return (
      this.deps.mapBoxViewerStore?.globeViewControlStore?.selectedGlobeViewId ??
      undefined
    )
  }

  // Live viewport pose for the editor's text boxes — reads the reactive viewport so the
  // boxes re-render and stay aligned with the displayed markups on zoom/tilt/rotate.
  public get textPose(): {
    scale: number
    pitch: number
    bearing: number
    perspective: number
  } {
    const viewport = this.deps.mapBoxViewerStore?.viewport
    // mapbox's camera distance (px). Used as the overlay's CSS perspective so a
    // tilted text box recedes like the GL geometry instead of tilting flat
    // (orthographically) and drifting off the ground plane.
    const cameraDistance =
      (
        this.map as unknown as {
          transform?: { cameraToCenterDistance?: number }
        }
      )?.transform?.cameraToCenterDistance ?? 0
    return {
      scale: textScaleForZoom(viewport?.zoom),
      pitch: viewport?.pitch ?? 0,
      bearing: viewport?.bearing ?? 0,
      perspective: cameraDistance,
    }
  }

  public get onEditPreciseLocation(): (() => void) | undefined {
    return this.deps.onEditPreciseLocation
  }

  // Static per editor, so no observable needed (cf. showLocationTool).
  public get legendExtraGroups(): IShortcutGroup[] {
    return this.deps.legendExtraGroups
  }

  // Idle = Select tool active with nothing selected; drawing or editing a markup
  // makes this false so the location pin stays inert.
  public get isIdleSelect(): boolean {
    return (
      this.activeTool === 'select' &&
      !this.selection &&
      !this.textStore.selectedId
    )
  }

  // A Text markup is the subject: typed into or selected (context toolbar shown).
  public get isTextSubject(): boolean {
    return !!this.textStore.editingId || !!this.textStore.selectedId
  }

  // Map wiring that must exist for the whole mount (copy-pill projection, text
  // deselect on map click, pointer-routing classes) even while terra-draw is deferred.
  private attachMap(map: MapboxMap): void {
    this.map = map
    this.textCapture = new MarkupTextCapture(
      map,
      map.getCanvasContainer(),
      this,
      {
        layerIds: this.deps.yieldToLayerIds,
        textLayerIds: this.deps.yieldToTextLayerIds,
      },
    )
    // Editing only runs while this component is mounted, so the class scopes
    // pointer-event routing to the edit session — map objects (Pin, pins) become
    // inert so they can't intercept the drawing pointer stream (see MarkupEditor.css).
    this.map.getContainer().classList.add('markup-editing')
    // While a non-select drawing tool is active, no map marker (foreign labels /
    // text) may catch a placement click — else a click on one is swallowed and the
    // vertex is skipped. Select clears it, restoring copy-pick (see MarkupEditor.css).
    this.disposeToolReaction = reaction(
      () => this.activeTool,
      tool =>
        this.map
          ?.getContainer()
          .classList.toggle('markup-drawing', tool !== 'select'),
    )
    this.map.on('move', this.handleMapMove)
    // Re-anchor the selection toolbar on every painted frame (Terra Draw repaints
    // the dragged item via rAF → 'render'), so a linked toolbar tracks the item in
    // lockstep with its rendering instead of lurching on coarser 'change' events.
    this.map.on('render', this.handleMapRender)
    // Detect a move of the selected markup (press on it + drag) to hide the
    // context toolbar during the drag — a native listener, since Terra Draw's
    // body-move emits no drag-start event.
    this.map
      .getCanvas()
      .addEventListener('pointerdown', this.handleCanvasPointerDown)
    // Clicking the bare map (not a text box / toolbar — those overlay the canvas
    // and swallow the event) releases a selected text box. Mapbox 'click' fires
    // only on a click without drag, so panning keeps the selection.
    this.map.on('click', this.handleMapClick)
  }

  private initController(map: MapboxMap): void {
    if (this.controller) return
    if (!this.map) this.attachMap(map)

    this.controller = new MarkupDrawController(map, {
      onChange: this.handleChange,
      onSelectionChange: this.handleSelectionChange,
      onToolChange: this.handleToolChange,
      onDrawingChange: this.handleDrawingChange,
      onEnterVertexEdit: this.handleEnterVertexEdit,
      getGlobeViewId: () => this.currentGlobeViewId,
      isMobile: this.isMobileEnv,
      canHandleKeys: () => this.ownsKeyboard,
      onCursorChange: cursor =>
        this.deps.mapBoxViewerStore.setMarkupEditorCursor(cursor),
      yieldToLayerIds: this.deps.yieldToLayerIds,
      yieldToTextLayerIds: this.deps.yieldToTextLayerIds,
    })
    this.controller.start()
    this.controller.setQuickZoomEnabled(this.isIdleSelect)

    // Switching the active globe mid-edit re-scopes the editor to that globe (stash
    // the current globe's work, load the target globe's markups). The camera reseat
    // is handled separately by FormPreciseLocationMap.
    this.disposeGlobeReaction = reaction(
      () => this.currentGlobeViewId,
      globeViewId => this.switchGlobe(globeViewId),
    )

    this.loadGlobe(this.currentGlobeViewId)
    // From here the session mount draws this editor's text, not the store's.
    this.deps.editorText?.attach(this.textSwap)

    // A deferred boot may arrive with a tool already picked (the pick is what
    // booted it) — bring the controller up to that tool.
    if (this.activeTool !== 'select') {
      this.controller.setTool(
        this.activeTool === 'text' ? 'select' : this.activeTool,
      )
    }

    this.deps.onReady?.({
      save: this.save,
      recordLocationChange: this.recordLocationChange,
      getFeatures: () => this.currentFeatures(),
      discardChanges: this.discardChanges,
      resetLocationBaseline: this.resetHistory,
      exitDrawing: this.exitDrawing,
    })
  }

  // Revert unsaved drawings to baseline (used on create cancel); touches only
  // the user's own shapes — context markups are a separate layer.
  @action.bound
  public discardChanges(): void {
    if (!this.controller) return
    const globeViewId = this.activeGlobeViewId
    this.editByGlobe.clear()
    if (globeViewId) this.loadGlobe(globeViewId)
  }

  private currentFeatures(): IMarkupFeatureData[] {
    return [
      ...(this.controller?.getMarkupFeatures() ?? []),
      ...this.textStore.getMarkupFeatures(),
    ]
  }

  private storeFeaturesForGlobe(globeViewId: string): IMarkupFeatureData[] {
    return this.markupsStore
      .forGlobe(
        this.markupsStore.getBySitePermitId(this.deps.sitePermitId),
        globeViewId,
      )
      .map(this.toFeatureData)
  }

  // Shared by globe-switch and undo/redo; clears selection chrome that would
  // otherwise dangle over the replaced content.
  @action
  private applyFeatures(
    geometry: IMarkupFeatureData[],
    loadText: () => void,
  ): void {
    this.isRestoring = true
    this.controller?.replaceAll(geometry)
    loadText()
    this.selection = null
    this.selectionScreen = null
    this.repositionText()
    this.isRestoring = false
  }

  // Live signature differs from the persisted baseline captured at first load.
  private get isActiveGlobeDirty(): boolean {
    const active = this.activeGlobeViewId
    return (
      !!active && this.headJson !== this.editByGlobe.get(active)?.baselineSig
    )
  }

  // Restore the globe's saved working copy + undo timeline if it has one, else load
  // fresh from the persisted store (seeding the baseline on the very first entry).
  private loadGlobe(globeViewId: string | undefined): void {
    this.activeGlobeViewId = globeViewId
    if (!globeViewId) return
    const saved = this.editByGlobe.get(globeViewId)
    if (saved?.history) {
      this.history = saved.history
      this.historyIndex = saved.index ?? 0
      this.restoreSnapshot(this.history[this.historyIndex])
      this.syncHistoryFlags()
      return
    }
    const persisted = this.storeFeaturesForGlobe(globeViewId)
    this.applyFeatures(
      persisted.filter(f => f.kind !== 'text'),
      () => this.textStore.load(persisted.filter(f => f.kind === 'text')),
    )
    // Seed the baseline before resetHistory, whose syncHistoryFlags reads it — else
    // the freshly-loaded globe reports dirty against a missing baseline.
    if (!saved) {
      this.editByGlobe.set(globeViewId, {
        loadedIds: persisted.map(f => f.id),
        baselineSig: JSON.stringify(this.snapshot()),
      })
    }
    this.resetHistory()
  }

  // Save the leaving globe's working copy + undo timeline if dirty (so returning
  // restores both); drop a clean globe's working state so it reloads from the store.
  @action.bound
  private switchGlobe(globeViewId: string | undefined): void {
    if (!this.controller || !globeViewId) return
    if (globeViewId === this.activeGlobeViewId) return
    this.stashActiveGlobe()
    this.loadGlobe(globeViewId)
  }

  // The working copy + undo timeline of the globe being left, so loadGlobe can put it
  // back; a clean globe's state is dropped so it reloads from the store instead.
  @action
  private stashActiveGlobe(): void {
    // Without the controller currentFeatures() sees no geometry: stashing would overwrite
    // a good working copy, clearing would drop it.
    if (!this.controller) return
    // Flush the debounced edit so the leaving globe's signature is current.
    if (this.captureTimer) this.commitHistory()
    const leaving = this.activeGlobeViewId
    const entry = leaving ? this.editByGlobe.get(leaving) : undefined
    if (!entry) return
    const features = this.currentFeatures()
    const loadedIds = new Set(entry.loadedIds)
    // A markup-first creation folds its initiating feature into the baseline, so a clean
    // globe can still hold one that was never persisted. save() gates the same way.
    const hasUnpersisted = features.some(f => !loadedIds.has(f.id))
    if (this.isActiveGlobeDirty || hasUnpersisted) {
      entry.features = features
      entry.history = this.history
      entry.index = this.historyIndex
      entry.headJson = this.headJson ?? undefined
    } else {
      entry.features = undefined
      entry.history = undefined
      entry.index = undefined
      entry.headJson = undefined
    }
  }

  private readonly toFeatureData = (markup: Markup): IMarkupFeatureData => ({
    id: markup.id,
    kind: markup.kind as MarkupKind,
    geometryType: markup.geometryType,
    coordinates: markup.coordinates,
    color: markup.color ?? DEFAULT_MARKUP_STYLE.color,
    strokeWidth: markup.strokeWidth ?? DEFAULT_MARKUP_STYLE.strokeWidth,
    opacity: markup.opacity ?? DEFAULT_MARKUP_STYLE.opacity,
    strokeStyle:
      (markup.strokeStyle as IMarkupFeatureData['strokeStyle']) ?? undefined,
    fillColor: markup.fillColor ?? undefined,
    fillStyle: (markup.fillStyle as FillStyle) ?? undefined,
    text: markup.text,
    shapeKind: markup.shapeKind as ShapeKind,
    lineType: markup.lineType as LineType,
    arrowheadStart: markup.arrowheadStart as ArrowheadType,
    arrowheadEnd: markup.arrowheadEnd as ArrowheadType,
    fontSize: markup.fontSize,
    bold: markup.bold,
    italic: markup.italic,
    underline: markup.underline,
    textAlign: markup.textAlign,
    boxWidth: markup.boxWidth,
    rotation: markup.rotation,
    globeViewId: markup.globeViewId,
  })

  @action.bound
  private handleChange(): void {
    this.captureHistory()
  }

  // Frame-synced re-anchor of the linked selection toolbar (see the 'render'
  // subscription). Pinned toolbars stay put; when nothing is selected it's a
  // no-op, and 'render' is quiet while the map is idle.
  @action.bound
  private handleMapRender(): void {
    if (!this.selection) return
    // The GL overlays are geographic (lng/lat), so Mapbox reprojects them itself
    // on camera moves — they only need re-setData while a live interaction is
    // changing geometry without a 'change' event (Terra Draw's body/vertex drag).
    // Calling it on every render otherwise just re-dirties the sources and
    // schedules the next render → a ~60fps idle loop while merely selected.
    if (this.interacting) {
      // Hot-overlay safety net (O(1): only the active feature) keeps the GL chrome
      // glued. Also reposition the HTML chrome that stays VISIBLE during a drag
      // (freehand box, path selection bbox). Rect/circle hit-targets + the rotate
      // pin are hidden via visibility, so repositioning them while invisible is
      // harmless. Sets React/MobX state only — no map setData → no repaint loop.
      this.controller?.syncSelectionOverlay()
      this.repositionShapeHandles()
      return
    }
    // Screen-space chrome (HTML handles + toolbar) does need re-anchoring on
    // camera moves; these set React/MobX state, not map sources, so they don't
    // feed the repaint loop.
    this.repositionShapeHandles()
    if (this.pinnedToolbarPos != null) return
    this.selection = this.controller?.getSelection() ?? null
    this.repositionSelection()
  }

  // Coalesce bursts (a drag fires many 'change' events; typing many keystrokes)
  // into one history entry by committing on a trailing debounce.
  private captureHistory(): void {
    // While a mobile picker sheet previews live, changes are visual-only — no undo
    // entry until Apply (see stylePreview).
    if (this.isRestoring || this.previewing) return
    if (this.captureTimer) clearTimeout(this.captureTimer)
    this.captureTimer = setTimeout(this.commitHistory, 250)
  }

  private snapshot(): IMarkupHistoryEntry {
    // Defensive copy: getLocation() hands back the parent's live pendingViewport
    // object, so clone it before freezing it into history.
    const location = this.deps.getLocation?.() ?? null
    return {
      geometry: this.controller?.getMarkupFeatures() ?? [],
      text: this.textStore.boxes.map(box => ({ ...box })),
      location: location ? { ...location } : null,
    }
  }

  @action.bound
  private commitHistory(): void {
    if (this.captureTimer) clearTimeout(this.captureTimer)
    this.captureTimer = null
    const snap = this.snapshot()
    const snapJson = JSON.stringify(snap)
    // Skip no-op captures: selecting/deselecting toggles Terra Draw's 'selected'
    // flag and emits 'change', but the committed geometry/text is unchanged —
    // recording it would force a wasted extra undo.
    if (this.history[this.historyIndex] && this.headJson === snapJson) return
    const MAX = 50
    this.history = this.history.slice(0, this.historyIndex + 1)
    this.history.push(snap)
    if (this.history.length > MAX) this.history = this.history.slice(-MAX)
    this.historyIndex = this.history.length - 1
    this.headJson = snapJson
    this.syncHistoryFlags()
  }

  @action.bound
  private resetHistory(): void {
    if (this.captureTimer) {
      clearTimeout(this.captureTimer)
      this.captureTimer = null
    }
    const snap = this.snapshot()
    const headJson = JSON.stringify(snap)
    this.history = [snap]
    this.historyIndex = 0
    this.headJson = headJson
    // Also re-anchor the dirty baseline: the desktop editor stays mounted across
    // creations, so a stale baseline reports a false dirty once the new pin differs.
    const active = this.activeGlobeViewId
    const entry = active ? this.editByGlobe.get(active) : undefined
    if (entry) entry.baselineSig = headJson
    this.syncHistoryFlags()
  }

  @action.bound
  private syncHistoryFlags(): void {
    this.canUndo = this.historyIndex > 0
    this.canRedo = this.historyIndex < this.history.length - 1
    // Dirty if the active globe differs from baseline, or another globe has unsaved work.
    const active = this.activeGlobeViewId
    const othersDirty = Array.from(this.editByGlobe).some(
      ([g, e]) =>
        g !== active && e.headJson != null && e.headJson !== e.baselineSig,
    )
    this.deps.onDirtyChange?.(
      active ? this.isActiveGlobeDirty || othersDirty : this.historyIndex > 0,
    )
  }

  @action.bound
  private restoreSnapshot(snap: IMarkupHistoryEntry): void {
    this.headJson = JSON.stringify(snap)
    this.applyFeatures(snap.geometry, () => this.textStore.restore(snap.text))
    this.deps.onRestoreLocation?.(snap.location)
  }

  // Commit the just-finished pin move as a unified history step (deduped if unchanged).
  @action.bound
  public recordLocationChange(): void {
    this.commitHistory()
  }

  @action.bound
  public selectTool(tool: MarkupTool): void {
    // Picking a real tool is the engagement point of the deferred-drawing flow.
    if (tool !== 'select') this.ensureDrawing()
    this.activeTool = tool
    // Text is handled by the HTML overlay, not Terra Draw — keep the controller
    // idle in select while the text tool is active.
    this.controller?.setTool(tool === 'text' ? 'select' : tool)
    // The Draw tool opens on its current sub-tool's defaults.
    if (tool === 'draw') this.applyDrawTypeDefaults(this.drawType)
    // The Shape tool opens on no fill + the default border color.
    if (tool === 'shape') this.applyShapeDefaults()
    // The Line tool opens on its current sub-type (default Line, not Arrow) at
    // the shared 4px default, so Arrow and Line draw at the same width.
    if (tool === 'arrow') {
      this.color = LINE_DEFAULT_STYLE.color
      this.controller?.setColor(LINE_DEFAULT_STYLE.color)
      this.strokeWidth = LINE_DEFAULT_STYLE.strokeWidth
      this.controller?.setStrokeWidth(LINE_DEFAULT_STYLE.strokeWidth)
      this.controller?.setStrokeStyle('solid')
      this.controller?.setLineType(this.lineType)
    }
    // Switching to a geometry drawing tool clears any selected text box.
    if (tool !== 'select' && tool !== 'text') this.textStore.select(null)
    // Text tool stays in select mode, so deselect geometry explicitly (no multi-edit).
    if (tool === 'text') this.controller?.deselect()
    // Picking a drawing tool counts as markup activity → deselect the location pin.
    if (tool !== 'select') this.deps.onMarkupActivity?.()
  }

  // "Done drawing" — called when the host's composer re-expands: finalize any
  // in-progress shape/text, drop every selection, return to Select.
  @action.bound
  public exitDrawing(): void {
    if (this.polygonDrawing) this.finishPolygon()
    else if (this.polylineDrawing) this.finishPolyline()
    const textId = this.textStore.selectedId
    if (textId) this.handleTextDone(textId)
    this.controller?.deselect()
    this.textStore.select(null)
    this.copySelectedId = null
    if (this.activeTool !== 'select') this.selectTool('select')
  }

  // Maps a single-key shortcut to a tool + sub-type. Returns true when handled.
  // (Callout 'C' is reserved — the tool is disabled for now.)
  @action.bound
  private applyToolShortcut(e: KeyboardEvent): boolean {
    switch (e.key.toLowerCase()) {
      case 'r':
        if (e.shiftKey) return false
        this.selectTool('shape')
        this.changeShapeKind('rectangle')
        return true
      case 'o':
        if (e.shiftKey) return false
        this.selectTool('shape')
        this.changeShapeKind('circle')
        return true
      case 'l':
        this.selectTool('arrow')
        this.changeLineType(e.shiftKey ? 'arrow' : 'line')
        return true
      case 'p':
        if (e.shiftKey) {
          this.selectTool('arrow')
          this.changeLineType('polyline')
          return true
        }
        this.selectTool('shape')
        this.changeShapeKind('polygon')
        return true
      case 't':
        if (e.shiftKey) return false
        this.selectTool('text')
        return true
      case 'd':
        if (e.shiftKey) return false
        this.selectTool('draw')
        return true
      case 'm':
        if (
          e.shiftKey ||
          !this.showLocationTool ||
          !this.onEditPreciseLocation
        ) {
          return false
        }
        // Like the other tool keys, a repeat press keeps the tool active rather
        // than toggling it off (Esc or picking another tool exits).
        if (!this.isLocationEditActive) this.onEditPreciseLocation()
        return true
      default:
        return false
    }
  }

  @action.bound
  private applyShapeDefaults(): void {
    this.fillColor = SHAPE_DEFAULT_STYLE.fillColor
    this.fillStyle = 'solid'
    this.color = SHAPE_DEFAULT_STYLE.color
    this.strokeWidth = SHAPE_DEFAULT_STYLE.strokeWidth
    // A fresh Shape session re-enables fill→border reflection.
    this.borderOverridden = false
    this.controller?.setBorderOverridden(false)
    this.controller?.setFillColor(SHAPE_DEFAULT_STYLE.fillColor)
    this.controller?.setFillStyle('solid')
    this.controller?.setColor(SHAPE_DEFAULT_STYLE.color)
    this.controller?.setStrokeWidth(SHAPE_DEFAULT_STYLE.strokeWidth)
    this.controller?.setStrokeStyle('solid')
  }

  @action.bound
  public changeFillColor(fillColor: string): void {
    this.fillColor = fillColor
    this.controller?.setFillColor(fillColor)
    // FigJam parity: the border tracks the fill until set explicitly. Apply
    // directly (not via changeColor) so the override flag stays off.
    if (!this.borderOverridden) {
      const border = deriveBorderColor(fillColor)
      if (border) {
        this.color = border
        this.controller?.setColor(border)
      }
    }
  }

  @action.bound
  public changeFillStyle(fillStyle: FillStyle): void {
    this.fillStyle = fillStyle
    this.controller?.setFillStyle(fillStyle)
  }

  // Sub-tools share the default color: the Highlighter is drawn translucent by
  // the tool, and the Eraser is thickness-only.
  @action.bound
  private applyDrawTypeDefaults(type: DrawType): void {
    // Draw tools have no border-style control → always solid; clear any
    // strokeStyle leaked from a prior tool/shape (matches shape/arrow resets).
    this.controller?.setStrokeStyle('solid')
    if (type === 'eraser') {
      this.strokeWidth = ERASER_THICKNESS
      this.controller?.setStrokeWidth(ERASER_THICKNESS)
      return
    }
    const preset =
      type === 'highlighter' ? HIGHLIGHTER_DEFAULT_STYLE : DRAW_DEFAULT_STYLE
    this.color = preset.color
    this.strokeWidth = preset.strokeWidth
    this.controller?.setColor(preset.color)
    this.controller?.setStrokeWidth(preset.strokeWidth)
  }

  // The controller auto-switches to Select after a segment is drawn; reflect
  // that in the toolbar without re-driving the controller.
  @action.bound
  private handleToolChange(tool: MarkupTool): void {
    this.activeTool = tool
  }

  @action.bound
  private handleDrawingChange(vertexCount: number): void {
    // Both multi-click tools report through onDrawingChange; route by active tool.
    const drawing = vertexCount >= 1
    this.polygonDrawing = this.activeTool === 'shape' && drawing
    this.polylineDrawing = this.activeTool === 'arrow' && drawing
  }

  @action.bound
  public finishPolygon(): void {
    this.controller?.finishPolygon()
  }

  @action.bound
  public cancelPolygon(): void {
    this.controller?.cancelPolygon()
  }

  @action.bound
  public finishPolyline(): void {
    this.controller?.finishPolyline()
  }

  @action.bound
  public cancelPolyline(): void {
    this.controller?.cancelPolyline()
  }

  @action.bound
  public changeColor(color: string): void {
    this.color = color
    this.controller?.setColor(color)
    // Explicit border choice — stop deriving it from the fill.
    this.borderOverridden = true
    this.controller?.setBorderOverridden(true)
  }

  @action.bound
  public changeStrokeWidth(width: number): void {
    this.strokeWidth = width
    this.controller?.setStrokeWidth(width)
  }

  @action.bound
  public changeShapeKind(kind: ShapeKind): void {
    this.shapeKind = kind
    this.controller?.setShapeKind(kind)
  }

  @action.bound
  public changeLineType(type: LineType): void {
    this.lineType = type
    this.controller?.setLineType(type)
  }

  @action.bound
  public changeDrawType(type: DrawType): void {
    this.drawType = type
    this.controller?.setDrawType(type)
    this.applyDrawTypeDefaults(type)
  }

  @action.bound
  private handleSelectionChange(id: string | null): void {
    // A different (or no) selection re-links the toolbar to its default anchor.
    this.pinnedToolbarPos = null
    if (!id) {
      this.selection = null
      this.selectionScreen = null
      this.vertexHintScreen = null
      this.dismissVertexEditHint()
      this.dismissVertexDeleteHint()
      this.rectangleHandleScreens = []
      this.circleHandleScreens = []
      this.freehandHandleScreens = []
      this.selectionBoxScreens = []
      this.selectionBoxAngle = null
      this.selectionBoxBboxAngle = null
      this.selectionBoxId = null
      this.rotateHandleScreen = null
      this.rotateStemScreen = null
      return
    }
    // Selecting a geometry markup clears any selected text box (one toolbar) and
    // any reference (copy-source) selection — editing wins over copy-picking.
    this.textStore.select(null)
    this.deps.onCopySelect?.(null)
    this.selection = this.controller?.getSelection() ?? null
    this.repositionSelection()
    this.repositionShapeHandles()
    // New selection is in 'move' stage; the delete tip re-shows on entering edit.
    this.dismissVertexDeleteHint()
    this.maybeShowVertexEditHint()
  }

  @action.bound
  private setPinnedToolbarPos(x: number, y: number): void {
    this.pinnedToolbarPos = this.clampToolbarPos(x, y)
  }

  // Keep the toolbar's rendered box inside the map container. Coords are in the
  // overlay's space; the box is centred on x and sits above y by its height +
  // TOOLBAR_GAP (the `translate(-50%, calc(-100% - TOOLBAR_GAP))` anchor).
  public clampToolbarPos(
    x: number,
    y: number,
    toolbar: HTMLDivElement | null = this.toolbarRef.current,
  ): { x: number; y: number } {
    const overlay = this.overlayRef.current
    if (!overlay || !this.map) return { x, y }
    const container = this.map.getContainer().getBoundingClientRect()
    const root = overlay.getBoundingClientRect()
    const left = container.left - root.left
    const top = container.top - root.top
    const right = left + container.width
    const bottom = top + container.height
    const halfW = (toolbar?.offsetWidth ?? 0) / 2
    const height = toolbar?.offsetHeight ?? 0
    const clamp = (v: number, lo: number, hi: number): number =>
      Math.min(Math.max(v, lo), hi <= lo ? lo : hi)
    return {
      x: clamp(x, left + halfW, right - halfW),
      // box top = y - height - 12 ≥ top; box bottom = y - 12 ≤ bottom
      y: clamp(y, top + height + TOOLBAR_GAP, bottom + TOOLBAR_GAP),
    }
  }

  @action.bound
  public startToolbarDrag(e: React.PointerEvent): void {
    e.stopPropagation()
    e.preventDefault()
    // Drag from the toolbar's current screen position (pinned, or the live anchor
    // if it was still linked); subsequent positions are pure screen coordinates.
    const base = this.pinnedToolbarPos ?? this.selectionScreen
    if (!base) return
    const startX = e.clientX
    const startY = e.clientY
    const baseX = base.x
    const baseY = base.y
    const onPointerMove = (ev: PointerEvent): void => {
      this.setPinnedToolbarPos(
        baseX + (ev.clientX - startX),
        baseY + (ev.clientY - startY),
      )
    }
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      this.toolbarDragCleanup = null
    }
    const onPointerUp = (): void => cleanup()
    this.toolbarDragCleanup = cleanup
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  @action.bound
  private resetTextToolbarPin(): void {
    this.pinnedTextToolbarPos = null
  }

  // Text-toolbar variant of clampToolbarPos: the text toolbar is LEFT-aligned
  // to its anchor (translate(0, ...)), so x clamps by the full width.
  // The selected text box's toolbar anchor: the rendered box's screen
  // top-center (measured from the DOM, so auto-width and rotation are exact),
  // in overlay coordinates. Falls back to the projected lng/lat anchor.
  public textToolbarAnchor(id: string): { x: number; y: number } | null {
    const el = document.querySelector<HTMLDivElement>(
      `[data-markup-text-editable="${id}"]`,
    )
    const overlay = this.overlayRef.current
    if (!el || !overlay) return null
    const rect = el.getBoundingClientRect()
    const root = overlay.getBoundingClientRect()
    return {
      x: rect.left + rect.width / 2 - root.left,
      y: rect.top - root.top,
    }
  }

  @action.bound
  private setPinnedTextToolbarPos(x: number, y: number): void {
    this.pinnedTextToolbarPos = this.clampToolbarPos(
      x,
      y,
      this.textToolbarRef.current,
    )
  }

  @action.bound
  public startTextToolbarDrag(e: React.PointerEvent): void {
    e.stopPropagation()
    e.preventDefault()
    const id = this.textStore.selectedId
    const base =
      this.pinnedTextToolbarPos ?? (id ? this.textToolbarAnchor(id) : null)
    if (!base) return
    const startX = e.clientX
    const startY = e.clientY
    const baseX = base.x
    const baseY = base.y
    const onPointerMove = (ev: PointerEvent): void => {
      this.setPinnedTextToolbarPos(
        baseX + (ev.clientX - startX),
        baseY + (ev.clientY - startY),
      )
    }
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      this.toolbarDragCleanup = null
    }
    const onPointerUp = (): void => cleanup()
    this.toolbarDragCleanup = cleanup
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  // Single always-on map-move handler: re-project both the geometry selection
  // toolbar and every text box so they track pan/zoom/tilt.
  @action.bound
  private handleMapMove(): void {
    this.repositionSelection()
    this.repositionText()
    this.repositionShapeHandles()
    this.repositionForeignCopy()
  }

  // map.project() is relative to the map CONTAINER, but our overlay (inset-0 of
  // its parent) isn't always aligned to the container — so translate projected
  // points into the overlay's local space. Keeps the overlay (text boxes +
  // context toolbars) pixel-aligned with the display-layer <Marker>s.
  private projectToOverlay(lng: number, lat: number): { x: number; y: number } {
    return projectToOverlay(this.map, lng, lat, this.overlayRef.current)
  }

  @action.bound
  private repositionSelection(): void {
    if (!this.selection || !this.map) {
      this.selectionScreen = null
      this.vertexHintScreen = null
      return
    }
    // Anchor the toolbar at the markup's screen bounding-box TOP-CENTER, so it
    // floats above the top edge (the -100% transform clears the markup itself)
    // rather than over the centroid. Fall back to the centroid anchor. The coaching
    // tip mirrors this at the BOTTOM-CENTER so it sits below the shape, clear of the
    // toolbar above.
    const coords = this.controller?.getSelectedCoords()
    if (coords && coords.length) {
      const screens = coords.map(([lng, lat]) =>
        this.projectToOverlay(lng, lat),
      )
      const { minX, maxX, minY, maxY } = screenBounds(screens)
      const x = (minX + maxX) / 2
      this.selectionScreen = { x, y: minY }
      this.vertexHintScreen = { x, y: maxY }
      return
    }
    this.selectionScreen = this.projectToOverlay(
      this.selection.anchor.lng,
      this.selection.anchor.lat,
    )
    this.vertexHintScreen = this.selectionScreen
  }

  // Single choke point for every drag/resize/rotate: besides hiding the toolbar,
  // it moves the selected markup's chrome into the controller's hot overlay for
  // the duration (one atomic source → nothing can split from the moving shape).
  @action.bound
  private setInteracting(value: boolean): void {
    this.interacting = value
    // For the duration of a Terra Draw drag, make every overlay pass-through so
    // the canvas drag is never interrupted by a marker/text box under the cursor.
    this.map?.getContainer().classList.toggle('markup-dragging', value)
    if (value) {
      this.controller?.beginActiveInteraction()
    } else {
      this.controller?.endActiveInteraction()
      this.repositionShapeHandles()
    }
  }

  // Window-level pointer drag for one gesture (rotate / resize). Only the pointer
  // that started it drives onMove — a second finger landing mid-drag is ignored —
  // and pointercancel ends it exactly like pointerup, so an interrupted touch
  // (second finger, system/edge gesture) can never strand the gesture with leaked
  // listeners and `interacting` stuck on.
  private runPointerDrag(
    e: React.PointerEvent,
    onMove: (ev: PointerEvent) => void,
    onEnd: () => void,
  ): void {
    const pointerId = e.pointerId
    const handleMove = (ev: PointerEvent): void => {
      if (ev.pointerId === pointerId) onMove(ev)
    }
    const finish = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
      onEnd()
    }
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
  }

  // A press on the SELECTED markup starts a move (Terra Draw drags it). Hide the
  // toolbar once the drag actually moves; re-anchor + show on release. A press on
  // the bare map pans (hitSelected false) — the toolbar keeps tracking.
  @action.bound
  private handleCanvasPointerDown(e: PointerEvent): void {
    if (!this.selection || !this.map) return
    const rect = this.map.getCanvas().getBoundingClientRect()
    const px = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    if (!this.controller?.hitSelected(px)) return

    // Touch has no right-click: hold a vertex ~500ms to delete it. Armed only in
    // vertex-edit (the controller no-ops the delete unless a vertex is hit). A drag
    // past a small threshold or an early release cancels it, so it never competes
    // with dragging a vertex to move it.
    const startX = e.clientX
    const startY = e.clientY
    let longPress: number | null = null
    const clearLongPress = (): void => {
      if (longPress != null) {
        clearTimeout(longPress)
        longPress = null
      }
    }
    if (e.pointerType === 'touch' && this.controller?.isEditingVertices) {
      longPress = window.setTimeout(() => {
        longPress = null
        if (this.controller?.deleteSelectedVertexNear(px)) {
          this.handleVertexDeleted()
        }
      }, 500)
    }

    // Dragging an open polyline's endpoint near the other end snaps + glues them,
    // closing it into a polygon — highlight the receiver while in range, commit on
    // release. A drag away from the other end just moves the vertex (no-op close).
    const closing = !!this.controller?.beginPolylineClose(px)

    let moved = false
    const onMove = (ev: PointerEvent): void => {
      if (
        longPress != null &&
        Math.hypot(ev.clientX - startX, ev.clientY - startY) > 8
      ) {
        clearLongPress()
      }
      if (closing) {
        this.controller?.updatePolylineClose({
          x: ev.clientX - rect.left,
          y: ev.clientY - rect.top,
        })
      }
      if (!moved) {
        moved = true
        this.setInteracting(true)
      }
      // Same pointer event Terra Draw is dragging the feature with (its canvas
      // listener runs first), so the hot overlay setData targets the same
      // upcoming paint as Terra Draw's own update.
      this.controller?.syncSelectionOverlay()
    }
    const onUp = (): void => {
      clearLongPress()
      if (closing) this.controller?.endPolylineClose()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      if (moved) {
        this.setInteracting(false)
        this.repositionSelection()
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  @action.bound
  private repositionShapeHandles(): void {
    const corners = this.map
      ? this.controller?.getSelectedRectangleCorners()
      : null
    this.rectangleHandleScreens = corners
      ? corners.map(([lng, lat]) => this.projectToOverlay(lng, lat))
      : []
    const circleCorners = this.map
      ? this.controller?.getSelectedCircleCorners()
      : null
    this.circleHandleScreens = circleCorners
      ? circleCorners.map(([lng, lat]) => this.projectToOverlay(lng, lat))
      : []
    this.repositionFreehandBox()
    this.repositionSelectionBox()
    this.repositionRotateHandle()
  }

  // Selection box for a path markup (line/polyline/polygon/arrow) — the kinds
  // with no tight outline box of their own. CIRCLE → its screen-aligned bounding
  // square (already upright). RECT (its casing IS the box) and FREEHAND (own box)
  // are skipped, so the box never doubles up. PATH → the MINIMUM-AREA oriented
  // rectangle of the projected vertices: it hugs the shape tightly AND keeps its
  // orientation (translates on a move, rotates rigidly — a screen-upright AABB
  // "breathes" instead) and never depends on a stored bbox angle (so old saved
  // markups don't render tilted under this map's fixed bearing). Corners
  // [TL, TR, BR, BL] in the box's frame; +v ("bottom") faces screen-down for the pin.
  @action.bound
  private repositionSelectionBox(): void {
    if (!this.selection || !this.map) {
      this.selectionBoxScreens = []
      return
    }
    // Circle: the bounding square already computed for its corner handles.
    if (this.controller?.getSelectedCircleEdge()) {
      this.selectionBoxScreens = this.circleHandleScreens
      return
    }
    const isRect = !!this.controller?.getSelectedRectangleCorners()
    const isFreehand = !!this.controller?.getSelectedFreehandPoints()
    const coords =
      !isRect && !isFreehand ? this.controller?.getSelectedCoords() : null
    // < 3 covers a pure 2-point Line/Arrow (1D) — no box, it shows its endpoints.
    // 3+ vertices (polyline/polygon) get the min-area box below.
    if (!coords || coords.length < 3) {
      this.selectionBoxScreens = []
      return
    }
    // Seed from the controller's PLANE-space min-area edge (down-faced), then advance
    // by the rotation delta each frame — tight + rigid, no flip. Build in plane space
    // via getSelectedChromeBox and project per-point so it stays glued under map pitch
    // (a screen-space fit skewed off a tilted shape).
    const planeAngle = this.controller?.getSelectedPathBoxPlaneAngle()
    if (planeAngle == null) {
      this.selectionBoxScreens = []
      return
    }
    const selId = this.selection?.id ?? null
    const bboxRad =
      ((this.controller?.getSelectedBboxAngle() ?? 0) * Math.PI) / 180
    let theta: number
    if (
      selId !== this.selectionBoxId ||
      this.selectionBoxAngle == null ||
      this.selectionBoxBboxAngle == null
    ) {
      // Down-face the seed at any camera: flip when the projected edge points up.
      const screenAngle = this.controller?.getSelectedPathBoxAngle() ?? 0
      theta = Math.cos(screenAngle) < 0 ? planeAngle + Math.PI : planeAngle
    } else {
      theta = this.selectionBoxAngle + (bboxRad - this.selectionBoxBboxAngle)
    }
    this.selectionBoxAngle = theta
    this.selectionBoxBboxAngle = bboxRad
    this.selectionBoxId = selId
    const pad = (this.controller?.getSelectedStrokeWidth() ?? 0) / 2 + 3
    const chrome = this.controller?.getSelectedChromeBox(pad, theta)
    if (!chrome) {
      this.selectionBoxScreens = []
      return
    }
    this.selectionBoxScreens = chrome.corners.map(([lng, lat]) =>
      this.projectToOverlay(lng, lat),
    )
  }

  // The rotate pin hangs off the box's bottom edge (in the box's own frame), 22px
  // out along the box's down-axis (top-edge-mid → bottom-edge-mid). The box angle
  // is tracked continuously (see repositionSelectionBox / chrome bboxAngle), so the
  // pin orbits WITH the shape and ends where the user released the drag — no flip.
  // Circles are excluded (rotation is a no-op); text rotates via its own box.
  @action.bound
  private repositionRotateHandle(): void {
    const isCircle = !!this.controller?.getSelectedCircleEdge()
    const isFreehand = !!this.controller?.getSelectedFreehandPoints()
    // A pure 2-point Line/Arrow has no box and no rotate pin — drag an endpoint to
    // reshape/rotate it (Excalidraw-style).
    const isTwoPointLine =
      !isFreehand && this.controller?.getSelectedCoords()?.length === 2
    if (!this.selection || !this.map || isCircle || isTwoPointLine) {
      this.rotateHandleScreen = null
      this.rotateStemScreen = null
      return
    }
    // Paths use their min-area selection box (continuous angle); rect/freehand use
    // the chrome box (continuous via bboxAngle). Corner order [TL, TR, BR, BL].
    let c: { x: number; y: number }[]
    if (this.selectionBoxScreens.length === 4) {
      c = this.selectionBoxScreens
    } else {
      const pad = isFreehand
        ? (this.controller?.getSelectedStrokeWidth() ?? 0) / 2 + 4
        : 0
      const chrome = this.controller?.getSelectedChromeBox(pad)
      if (!chrome) {
        this.rotateHandleScreen = null
        this.rotateStemScreen = null
        return
      }
      c = chrome.corners.map(([lng, lat]) => this.projectToOverlay(lng, lat))
    }
    const topMid = { x: (c[0].x + c[1].x) / 2, y: (c[0].y + c[1].y) / 2 }
    const botMid = { x: (c[2].x + c[3].x) / 2, y: (c[2].y + c[3].y) / 2 }
    const len = Math.hypot(botMid.x - topMid.x, botMid.y - topMid.y) || 1
    this.rotateStemScreen = botMid
    this.rotateHandleScreen = {
      x: botMid.x + ((botMid.x - topMid.x) / len) * 22,
      y: botMid.y + ((botMid.y - topMid.y) / len) * 22,
    }
  }

  @action.bound
  public startRotate(e: React.PointerEvent): void {
    e.stopPropagation()
    e.preventDefault()
    if (!this.map) return
    const canvas = this.map.getCanvas().getBoundingClientRect()
    if (
      !this.controller?.beginRotate({
        x: e.clientX - canvas.left,
        y: e.clientY - canvas.top,
      })
    ) {
      return
    }
    // Capture the pointer so the release never reaches the map canvas — a
    // pointerup on the bare map reads as a deselect click (Terra Draw / map
    // click handlers) and would drop the selection right after rotating.
    const handleEl = e.currentTarget as HTMLElement
    handleEl.setPointerCapture?.(e.pointerId)
    this.setInteracting(true)
    this.runPointerDrag(
      e,
      ev => {
        this.controller?.rotateSelectedToPoint(
          { x: ev.clientX - canvas.left, y: ev.clientY - canvas.top },
          ev.shiftKey,
        )
      },
      () => {
        this.controller?.endRotate()
        this.setInteracting(false)
        this.repositionSelection()
        this.repositionShapeHandles()
      },
    )
  }

  // The freehand stroke's oriented selection box: the controller computes the
  // plane-frame chrome (camera-independent), the editor only PROJECTS its
  // corners — so the box hugs the stroke at any pitch/bearing and turns with
  // it live during a rotate drag. Recomputed each render frame.
  @action.bound
  private repositionFreehandBox(): void {
    const points = this.map
      ? this.controller?.getSelectedFreehandPoints()
      : null
    if (!points || points.length < 2) {
      this.freehandHandleScreens = []
      return
    }
    // The geometry is the centerline; pad out to the rendered stroke's edges
    // (half the stroke width) plus a small margin so the box clears the stroke.
    const pad = (this.controller?.getSelectedStrokeWidth() ?? 0) / 2 + 4
    const chrome = this.controller?.getSelectedChromeBox(pad)
    if (!chrome) {
      this.freehandHandleScreens = []
      return
    }
    // Corner order [TL, TR, BR, BL] — matches resizeSelectedFreehandBbox.
    this.freehandHandleScreens = chrome.corners.map(([lng, lat]) =>
      this.projectToOverlay(lng, lat),
    )
  }

  @action.bound
  public startFreehandResize(e: React.PointerEvent, index: number): void {
    e.stopPropagation()
    e.preventDefault()
    if (!this.map) return
    this.setInteracting(true)
    const canvas = this.map.getCanvas().getBoundingClientRect()
    this.runPointerDrag(
      e,
      ev => {
        this.controller?.resizeSelectedFreehandBbox(index, {
          x: ev.clientX - canvas.left,
          y: ev.clientY - canvas.top,
        })
      },
      () => {
        this.setInteracting(false)
        this.repositionSelection()
      },
    )
  }

  @action.bound
  public startCircleResize(e: React.PointerEvent, index: number): void {
    e.stopPropagation()
    e.preventDefault()
    if (!this.map) return
    this.setInteracting(true)
    const canvas = this.map.getCanvas().getBoundingClientRect()
    this.runPointerDrag(
      e,
      ev => {
        this.controller?.resizeSelectedCircle(
          {
            x: ev.clientX - canvas.left,
            y: ev.clientY - canvas.top,
          },
          index,
        )
      },
      () => {
        this.setInteracting(false)
        this.repositionSelection()
      },
    )
  }

  @action.bound
  public startRectangleResize(e: React.PointerEvent, index: number): void {
    e.stopPropagation()
    e.preventDefault()
    if (!this.map) return
    this.setInteracting(true)
    const canvas = this.map.getCanvas().getBoundingClientRect()
    this.runPointerDrag(
      e,
      ev => {
        this.controller?.resizeSelectedRectangle(index, {
          x: ev.clientX - canvas.left,
          y: ev.clientY - canvas.top,
        })
      },
      () => {
        this.setInteracting(false)
        this.repositionSelection()
      },
    )
  }

  @action.bound
  private repositionText(): void {
    if (!this.map) return
    // Runs every camera-move frame, for the DOM boxes only (the rest are GL). Without this bail,
    // fresh empty Maps would ping every observer of these refs once per frame.
    const domIds = this.textSwap.domTextIds
    if (!domIds.length && !this.textScreens.size) return
    const boxes = this.textStore.boxes.filter(box => domIds.includes(box.id))
    this.textScreens = new Map(
      boxes.map(box => [box.id, this.projectToOverlay(box.lng, box.lat)]),
    )
    this.textMapScreens = new Map(
      boxes.map(box => {
        const point = this.map.project([box.lng, box.lat])
        return [box.id, { x: point.x, y: point.y }]
      }),
    )
  }

  @action.bound
  public handleTextPlaceClick(e: React.MouseEvent): void {
    if (!this.map) return
    const canvas = this.map.getCanvas().getBoundingClientRect()
    const lngLat = this.map.unproject([
      e.clientX - canvas.left,
      e.clientY - canvas.top,
    ])
    // Seed rotation = bearing so the commit transform's rotateZ(-bearing) leaves it upright.
    this.textStore.place(lngLat.lng, lngLat.lat, this.textPose.bearing)
    this.selectTool('select')
    this.repositionText()
  }

  // For the text capture (IMarkupTextCaptureHost).
  public get canClaimText(): boolean {
    return (
      this.activeTool === 'select' &&
      !this.isLocationEditActive &&
      !this.interacting
    )
  }

  public isEditingText(id: string): boolean {
    return this.textStore.editingId === id
  }

  // Over resting text: the text box's move cursor, and no geometry outline from underneath.
  public setTextHover(isOver: boolean): void {
    if (isOver) this.controller?.clearHover()
    this.deps.mapBoxViewerStore.setMarkupEditorCursor(isOver ? 'move' : null)
  }

  // A tap on a text box, from whichever element took it: a double-tap edits, a tap selects.
  @action.bound
  public handleTextTap(id: string, x: number, y: number, t: number): void {
    if (this.textTaps.tap(id, x, y, t) === 'edit') {
      this.textStore.edit(id)
    } else {
      this.handleTextSelect(id)
    }
  }

  public readonly cancelTextTap = (): void => this.textTaps.cancel()

  @action.bound
  public handleTextSelect(id: string): void {
    // Selecting text deselects any geometry and clears the foreign copy-selection
    // so only one toolbar shows — editing wins over copy-picking (mirrors the
    // geometry path; text is HTML so the map click handler never clears it).
    this.controller?.deselect()
    this.selection = null
    this.selectionScreen = null
    this.deps.onCopySelect?.(null)
    this.textStore.select(id)
  }

  @action.bound
  public handleTextMove(id: string, lng: number, lat: number): void {
    this.textStore.move(id, lng, lat)
    this.repositionText()
  }

  // True only for the brief window around a pointerdown inside the text style
  // toolbar. Lets handleTextBlur recognize the blur that pointerdown triggers on the
  // editable as a styling interaction (keep editing) rather than an abandon. Reset
  // next tick — the blur it guards fires synchronously before then.
  private textToolbarPointerDown = false

  public markTextToolbarInteraction(): void {
    this.textToolbarPointerDown = true
    setTimeout(() => {
      this.textToolbarPointerDown = false
    }, 0)
  }

  @action.bound
  public handleTextBlur(id: string): void {
    // A pointerdown in the style toolbar just blurred the editable — that's a styling
    // interaction, not an abandon: keep the box editing (and flat) so changing
    // colour/font from edit mode stays in edit mode, and the caret returns via
    // refocusEditingText. Otherwise the user clicked away: commit and leave editing.
    if (this.textToolbarPointerDown) return
    this.textStore.stopEditing()
    this.textStore.discardIfEmpty(id)
    this.repositionText()
  }

  // Enter/Escape inside a box: commit, drop it if empty, and fully deselect so
  // the context toolbar hides.
  @action.bound
  private handleTextDone(id: string): void {
    this.textStore.discardIfEmpty(id)
    this.textStore.select(null)
    this.repositionText()
  }

  // Click on the bare map releases the selected text box (toolbar hides). Also
  // hit-tests the dimmed foreign (other-workflow) reference geometry so clicking
  // its body picks it as a copy source (the Copy toolbar then appears). Deferred
  // so Terra Draw's own selection settles first — an editable hit wins.
  @action.bound
  private handleMapClick(e: MapMouseEvent): void {
    if (this.textStore.selectedId) this.textStore.select(null)
    const refLayers = [
      'markup-ref-fill',
      'markup-ref-fill-outline',
      'markup-ref-fill-outline-dashed',
      'markup-ref-line',
      'markup-ref-line-dashed',
    ].filter(id => this.map.getLayer(id))
    const hit = refLayers.length
      ? this.map.queryRenderedFeatures(e.point, { layers: refLayers })
      : []
    // Foreign text draws over foreign geometry, so its hit wins.
    const markupId =
      queryTextMarkups(this.map, e.point, ['markup-ref-text'])[0] ??
      (hit.find(f => f.properties?.markupId)?.properties?.markupId as
        | string
        | undefined)
    this.copySelectTimer = setTimeout(() => {
      this.copySelectTimer = null
      if (this.controller?.getSelection()) return
      this.deps.onCopySelect?.(markupId ?? null)
    }, 0)
  }

  @action.bound
  private repositionForeignCopy(): void {
    const id = this.copySelectedId
    const markup = id ? this.markupsStore.byId.get(id) : null
    const coords = markup?.coordinates
    if (!coords?.length || !this.map) {
      this.foreignSelectedScreen = null
      return
    }
    const screens = coords.map(c =>
      this.projectToOverlay(c.longitude, c.latitude),
    )
    const { minX, maxX, minY } = screenBounds(screens)
    this.foreignSelectedScreen = {
      x: (minX + maxX) / 2,
      y: minY,
    }
  }

  // Copy toolbar action: duplicate the picked foreign markup into THIS workflow
  // (same path as Cmd/Ctrl+C → V), then clear the foreign selection.
  @action.bound
  public copyForeignNow(): void {
    if (this.copySelectedMarkup()) this.pasteMarkup()
    this.deps.onCopySelect?.(null)
  }

  @action.bound
  public deleteSelectedText(): void {
    this.textStore.deleteSelected()
    this.repositionText()
  }

  // After a toolbar style change, return the caret ONLY if the user was already
  // typing (edit mode) so they can keep typing. Styling a merely-selected box must
  // NOT pull it into edit mode — it stays a selected 3D object. The color/size menus
  // portal a popup that takes focus, so defer to the next frame — past the popover's
  // own focus restoration on close.
  private refocusEditingText(): void {
    const id = this.textStore.selectedId
    if (!id || this.textStore.editingId !== id) return
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLDivElement>(
        `[data-markup-text-editable="${id}"]`,
      )
      if (!el) return
      el.focus()
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      selection?.removeAllRanges()
      selection?.addRange(range)
    })
  }

  @action.bound
  public changeTextColor(color: string): void {
    this.textStore.setColor(color)
    this.refocusEditingText()
  }

  @action.bound
  public changeTextFillColor(fillColor: string): void {
    this.textStore.setFillColor(fillColor)
    this.refocusEditingText()
  }

  @action.bound
  public changeTextFontSize(size: number): void {
    this.textStore.setFontSize(size)
    this.refocusEditingText()
  }

  @action.bound
  public toggleTextBold(): void {
    this.textStore.toggleBold()
    this.refocusEditingText()
  }

  @action.bound
  public toggleTextItalic(): void {
    this.textStore.toggleItalic()
    this.refocusEditingText()
  }

  @action.bound
  public toggleTextUnderline(): void {
    this.textStore.toggleUnderline()
    this.refocusEditingText()
  }

  @action.bound
  public changeSelectedColor(color: string): void {
    this.controller?.setColor(color)
    // Explicit border choice on this markup — stop deriving it from its fill.
    this.controller?.setSelectedBorderOverridden(true)
    if (this.selection)
      this.selection = { ...this.selection, color, borderOverridden: true }
  }

  @action.bound
  public changeSelectedWidth(width: number): void {
    this.controller?.setStrokeWidth(width)
    if (this.selection)
      this.selection = { ...this.selection, strokeWidth: width }
  }

  @action.bound
  public changeSelectedStrokeStyle(style: StrokeStyle): void {
    this.controller?.setStrokeStyle(style)
    if (this.selection)
      this.selection = { ...this.selection, strokeStyle: style }
  }

  @action.bound
  public changeSelectedArrowhead(
    position: ArrowheadPosition,
    type: ArrowheadType,
  ): void {
    this.controller?.setArrowhead(position, type)
    if (this.selection)
      this.selection = {
        ...this.selection,
        arrowheadStart:
          position === 'start' ? type : this.selection.arrowheadStart,
        arrowheadEnd: position === 'end' ? type : this.selection.arrowheadEnd,
      }
  }

  @action.bound
  public changeSelectedFillColor(fillColor: string): void {
    this.controller?.setFillColor(fillColor)
    if (this.selection) this.selection = { ...this.selection, fillColor }
    // FigJam parity: derive the border from the fill unless the user has set
    // this markup's border explicitly.
    if (this.selection && !this.selection.borderOverridden) {
      const border = deriveBorderColor(fillColor)
      if (border) {
        this.controller?.setColor(border)
        this.selection = { ...this.selection, color: border }
      }
    }
  }

  @action.bound
  public changeSelectedFillStyle(fillStyle: FillStyle): void {
    this.controller?.setFillStyle(fillStyle)
    if (this.selection) this.selection = { ...this.selection, fillStyle }
  }

  @action.bound
  public deleteSelectedMarkup(): void {
    this.controller?.deleteSelected()
  }

  // While a sheet is open, changeSelected*/changeText* apply LIVE to the map but
  // history is suppressed (see captureHistory). Apply commits one entry; Cancel
  // restores the style snapshotted at open.
  @action.bound
  public startStylePreview(): void {
    const s = this.selection
    const t = this.textStore.selected
    this.previewOriginal = s
      ? {
          geometry: {
            color: s.color,
            strokeWidth: s.strokeWidth,
            strokeStyle: s.strokeStyle,
            fillColor: s.fillColor,
            fillStyle: s.fillStyle,
            arrowheadStart: s.arrowheadStart,
            arrowheadEnd: s.arrowheadEnd,
            borderOverridden: s.borderOverridden,
          },
        }
      : t
        ? {
            text: {
              color: t.color,
              fillColor: t.fillColor,
              fontSize: t.fontSize,
            },
          }
        : null
    this.previewing = true
  }

  @action.bound
  public applyStylePreview(): void {
    this.previewing = false
    this.previewOriginal = null
    this.captureHistory()
  }

  @action.bound
  public cancelStylePreview(): void {
    const o = this.previewOriginal
    if (o?.geometry) {
      const g = o.geometry
      this.changeSelectedColor(g.color)
      this.changeSelectedWidth(g.strokeWidth)
      this.changeSelectedStrokeStyle(g.strokeStyle)
      this.changeSelectedFillColor(g.fillColor)
      this.changeSelectedFillStyle(g.fillStyle)
      this.changeSelectedArrowhead('start', g.arrowheadStart)
      this.changeSelectedArrowhead('end', g.arrowheadEnd)
      // Restore the original override state last — the color setters above flip
      // it as a side effect; the snapshot is the source of truth on cancel.
      this.controller?.setSelectedBorderOverridden(g.borderOverridden)
      if (this.selection)
        this.selection = {
          ...this.selection,
          borderOverridden: g.borderOverridden,
        }
    } else if (o?.text) {
      this.changeTextColor(o.text.color)
      this.changeTextFillColor(o.text.fillColor)
      this.changeTextFontSize(o.text.fontSize)
    }
    this.previewing = false
    this.previewOriginal = null
  }

  public get stylePreview(): IStylePreview {
    return {
      begin: this.startStylePreview,
      apply: this.applyStylePreview,
      cancel: this.cancelStylePreview,
    }
  }

  @action.bound
  public undo(): void {
    // Commit any pending (debounced) change first so it becomes the redo target.
    if (this.captureTimer) this.commitHistory()
    if (this.historyIndex <= 0) return
    this.historyIndex--
    this.restoreSnapshot(this.history[this.historyIndex])
    this.syncHistoryFlags()
  }

  @action.bound
  public redo(): void {
    if (this.captureTimer) this.commitHistory()
    if (this.historyIndex >= this.history.length - 1) return
    this.historyIndex++
    this.restoreSnapshot(this.history[this.historyIndex])
    this.syncHistoryFlags()
  }

  private persistFeature(f: IMarkupFeatureData): void {
    const markup = new Markup(
      f.id,
      this.deps.projectId,
      this.deps.sitePermitId,
      f.kind,
      f.geometryType,
      f.coordinates,
      f.color,
      f.strokeWidth,
      f.opacity,
      undefined,
      f.text,
    )
    // The creation-time globe, never the save-time one.
    markup.globeViewId = f.globeViewId
    markup.shapeKind = f.shapeKind
    markup.lineType = f.lineType
    markup.arrowheadStart = f.arrowheadStart
    markup.arrowheadEnd = f.arrowheadEnd
    markup.strokeStyle = f.strokeStyle
    markup.fillColor = f.fillColor
    markup.fillStyle = f.fillStyle
    markup.fontSize = f.fontSize
    markup.bold = f.bold
    markup.italic = f.italic
    markup.underline = f.underline
    markup.textAlign = f.textAlign
    markup.boxWidth = f.boxWidth
    markup.rotation = f.rotation
    // Creation flow: the workflow has no real id yet, so buffer locally (no
    // SaveMarkup network call) — the creation store flushes these to the real
    // id once the workflow is created. Edit flow persists immediately.
    if (this.deps.deferred) this.markupsStore.savePending(markup)
    else this.markupsStore.save(markup)
  }

  private save = (): void => {
    this.textStore.discardAllEmpty()
    if (this.captureTimer) this.commitHistory()
    // Persist and reconcile only globes that changed — the active one from its live
    // state, others from their saved working copy. Clean / never-opened globes untouched.
    const active = this.activeGlobeViewId
    this.editByGlobe.forEach((entry, globeViewId) => {
      const isActive = globeViewId === active

      const dirty = isActive
        ? this.isActiveGlobeDirty
        : entry.headJson != null && entry.headJson !== entry.baselineSig
      // Mid-rebind the controller is gone and currentFeatures() would report no geometry, so
      // every loaded id would land in removedIds and be deleted. The rebind stashes before
      // detaching, so the working copy is the truthful source until it is back.
      const features =
        isActive && this.controller
          ? this.currentFeatures()
          : (entry.features ?? [])
      // A markup-first creation folds the initiating feature into the baseline
      // (startCreation → resetLocationBaseline), so the globe reads clean while
      // holding never-persisted features — dirtiness alone must not gate the save.
      const loadedIds = new Set(entry.loadedIds)
      const hasUnpersisted = features.some(f => !loadedIds.has(f.id))
      if (!dirty && !hasUnpersisted) return
      features.forEach(f => this.persistFeature(f))
      const currentIds = new Set(features.map(f => f.id))
      const removedIds = entry.loadedIds.filter(id => !currentIds.has(id))
      if (this.deps.deferred) this.markupsStore.removeLocal(removedIds)
      else this.markupsStore.removeMany(removedIds)
    })

    this.deps.onClose()
  }
}
